/**
 * Mirrors supabase/functions/_shared/personalizeEmailCopy.ts for preview UI.
 * Keep in sync when changing prompts server-side.
 */

export const PERSONALIZATION_SYSTEM_PROMPT = `You personalize marketing emails for individual subscribers.

You receive READER_PROFILE as JSON. Each item in "fields" has:
- name: human-readable field name
- value: data we have about this reader
- how_to_use: how to apply that data in copy (follow this)

Rules:
- Make copy clearer and more relevant to this reader; prefer slightly shorter, tighter prose when the original is generic.
- Use at most one strong angle from the profile (role, a custom field, or channels).
- Preserve all factual claims, links, URLs, CTAs, and HTML structure from the original.
- Do not invent facts, offers, meetings, or quotes not supported by READER_PROFILE or the original email.
- Do not add LinkedIn URLs unless the original already includes them.
- Output valid JSON only:
  {"subject":"...","body_html":"...","changes":[{"area":"...","rationale":"..."}]}
- body_html must remain valid HTML. Target roughly 10–25% fewer words in body text when the original is wordy; never cut below half the original length.`;

const MAX_BODY_CHARS = 24_000;

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max) + '…';
}

export function buildPersonalizationUserPromptFromPayload(
  contextPayload: Record<string, unknown>,
  baseSubject: string,
  baseBody: string,
): string {
  return [
    'READER_PROFILE (JSON):',
    JSON.stringify(contextPayload, null, 2),
    '',
    '--- ORIGINAL SUBJECT ---',
    baseSubject,
    '--- ORIGINAL BODY HTML ---',
    truncate(baseBody, MAX_BODY_CHARS),
    '',
    'Return JSON with personalized subject, body_html, and a short changes array describing what you tuned.',
  ].join('\n');
}

export interface PersonalizationPromptView {
  model: string;
  system: string;
  user: string;
}

export function buildPersonalizationPromptView(
  contextPayload: Record<string, unknown>,
  baseSubject: string,
  baseBody: string,
  model = 'gpt-4o-mini',
): PersonalizationPromptView {
  return {
    model,
    system: PERSONALIZATION_SYSTEM_PROMPT,
    user: buildPersonalizationUserPromptFromPayload(contextPayload, baseSubject, baseBody),
  };
}
